/*
|--------------------------------------------------------------------------
| Common Behaviors
|--------------------------------------------------------------------------
|
| description
|
*/
function initCommonBehaviors() {
  const $SubmitButtons = document.querySelectorAll<HTMLButtonElement>('.submit-button');
  if ($SubmitButtons.length) {
    $SubmitButtons.forEach(($SubmitButton) => {
      $SubmitButton.addEventListener('click', (e) => {
        const { target } = e;
        if (!(target instanceof HTMLButtonElement)) return;

        // フォームを解決: data-form_id / data-form-id -> button.form -> closest('form')
        // NOTE: data 属性の書き方差分（underscore / kebab）に強くする
        const formId =
          target.getAttribute('data-form_id')
          || target.getAttribute('data-form-id')
          || (target as any).dataset?.form_id
          || (target as any).dataset?.formId
          || null;

        let form: HTMLFormElement | null = null;
        if (formId) {
          form = document.getElementById(formId) as HTMLFormElement | null;
        }
        if (!form) {
          form = (target.form as HTMLFormElement | null) || (target.closest('form') as HTMLFormElement | null);
        }

        if (!form) {
          // フォームが見つからない場合は、通常のブラウザ submit に任せる（type="submit" のフェイルセーフ）
          // eslint-disable-next-line no-console
          console.warn('submit-button: target form not found (falling back to native submit)', target);
          return;
        }

        // ここまで来たら JS で送信をハンドリングするので、ネイティブ submit を止める
        e.preventDefault();

        // 元のテキストを data 属性に保持（復元用）
        if (!target.dataset.originalText) target.dataset.originalText = target.innerText || '';

        // 二重送信防止のため無効化して送信
        target.disabled = true;
        target.innerText = 'Processing...';

        // 同一URLなら _reload 付与で強制遷移
        const method = (form.getAttribute('method') || 'get').toLowerCase();
        if (method === 'get') {
          const buildNextUrl = () => {
            try { disableHiddenDuplicateInputs(form.id || undefined); } catch (e) { /* noop */ }
            const action = new URL(form.action, window.location.href);
            const data = new FormData(form);
            data.forEach((value, key) => {
              if (typeof value === 'string') {
                action.searchParams.append(key, value);
              }
            });
            return action;
          };

          const nextUrl = buildNextUrl().toString();
          const currentUrl =
            window.location.origin
            + window.location.pathname
            + window.location.search
            + window.location.hash;

          if (nextUrl === currentUrl) {
            const action = buildNextUrl();
            action.searchParams.set('_reload', Date.now().toString());
            window.location.assign(action.toString());
            return;
          }
        }


          try {
            try { disableHiddenDuplicateInputs(form.id || undefined); } catch (e) { /* noop */ }
          /**
           * IMPORTANT:
           * - `form.submit()` は submit イベントを発火しないため、
           *   reCAPTCHA(v3) の `form.addEventListener('submit', ...)` が動かず
           *   `g-recaptcha-response` が送られないことがある。
           *
           * 対策:
           * - 可能なら `form.requestSubmit()` を使い、通常の submit フロー（イベント発火）で送信する。
           * - `requestSubmit` が無い環境では、一時的な submit ボタンを作って click することで
           *   submit イベントを発火させる（`form.submit()` は最後のフォールバック）。
           */
          if (typeof (form as any).requestSubmit === 'function') {
            /**
             * `requestSubmit(submitter)` は submitter が「submit ボタン」の場合のみ渡せる。
             * x-submit は type="button" のケースがあり、その場合は例外になるため、
             * submit ボタンでないときは引数なしで requestSubmit() する。
             */
            const isSubmitButton = (target.type === 'submit');
            if (isSubmitButton) {
              (form as any).requestSubmit(target);
            } else {
              (form as any).requestSubmit();
            }
            return;
          }

          // Fallback: 一時的な submit ボタンを作って click する（submit イベントが発火する）
          try { disableHiddenDuplicateInputs(form.id || undefined); } catch (e) { /* noop */ }
          const tmp = document.createElement('button');
          tmp.type = 'submit';
          tmp.style.display = 'none';
          form.appendChild(tmp);
          tmp.click();
          tmp.remove();
        } catch (err) {
          // 送信に失敗したらボタンを再有効化して元テキストに戻す
          // eslint-disable-next-line no-console
          console.error('form.submit() failed', err);
          try {
            target.disabled = false;
            target.innerText = target.dataset.originalText || '';
          } catch (e) { /* noop */ }
        }
      });
    });
  }
}

// ブラウザバックや bfcache 復帰時に、送信ボタンを再有効化してテキストを復元する
function restoreSubmitButtonsAfterNavigation() {
  document.querySelectorAll<HTMLButtonElement>('.submit-button').forEach((btn) => {
    try {
      btn.disabled = false;
      const orig = btn.dataset.originalText;
      if (typeof orig === 'string' && orig.length > 0) btn.innerText = orig;
    } catch (e) {
      // noop
    }
  });
}

// プロジェクト検索 選択解除した項目が残る問題解消
// 表示されていない（offsetParent が null）同名 input を disabled にする
function disableHiddenDuplicateInputs(formId = 'searchStoryForm') {
  try {
    const form = document.getElementById(formId) as HTMLFormElement | null;
    if (!form) return;

    const inputs = Array.from(form.querySelectorAll<HTMLInputElement>('input[name]'));
    const groups: Record<string, HTMLInputElement[]> = {};
    inputs.forEach((i) => {
      const n = i.name || '';
      if (!n) return;
      if (!groups[n]) groups[n] = [];
      groups[n].push(i);
    });

    Object.keys(groups).forEach((name) => {
      const arr = groups[name];
      if (!arr || arr.length <= 1) {
        if (arr && arr[0]) {
          try { arr[0].disabled = false; } catch (e) { /* noop */ }
        }
        return;
      }

      // 同じ name の中で value ごとに分け、各 value の中で表示されている label を優先して有効化する。
      // 見えているラベルがなければ input 自身の可視性を使う。どれも見えていなければ全て有効にする。
      const byValue: Record<string, HTMLInputElement[]> = {};
      arr.forEach((el) => {
        const v = el.value || '';
        if (!byValue[v]) byValue[v] = [];
        byValue[v].push(el);
      });

      Object.keys(byValue).forEach((val) => {
        const inputs = byValue[val];
        if (!inputs || inputs.length <= 1) {
          if (inputs && inputs[0]) try { inputs[0].disabled = false; } catch (e) { /* noop */ }
          return;
        }

        // ラベル優先で可視なものを探す
        let chosenIndex = -1;
        for (let i = 0; i < inputs.length; i++) {
          const el = inputs[i];
          try {
            if (el.id) {
              const lab = form.querySelector<HTMLLabelElement>(`label[for="${el.id}"]`);
              if (lab) {
                const cs = getComputedStyle(lab);
                const rect = lab.getBoundingClientRect();
                if (cs.display !== 'none' && cs.visibility !== 'hidden' && rect.width > 0 && rect.height > 0) {
                  chosenIndex = i;
                  break;
                }
              }
            }
          } catch (e) { /* noop */ }
        }

        if (chosenIndex >= 0) {
          // chosenIndex を有効にし、それ以外は無効にする。
          // もし他の要素もチェック済みであっても、送信は一つに揃えるため無効化する。
          inputs.forEach((el, idx) => {
            try { el.disabled = !(idx === chosenIndex); } catch (e) { /* noop */ }
          });
          return;
        }

        // ラベルが見つからなければ input 自身の表示を基準にする
        let inputVisibleIndex = -1;
        for (let i = 0; i < inputs.length; i++) {
          const el = inputs[i];
          try {
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              inputVisibleIndex = i;
              break;
            }
          } catch (e) { /* noop */ }
        }

        if (inputVisibleIndex >= 0) {
          inputs.forEach((el, idx) => { try { el.disabled = !(idx === inputVisibleIndex); } catch (e) { /* noop */ } });
        } else {
          // どれも見えていない場合は、複数がチェック済みであればその中から1つだけ有効化する。
          const checkedIndexes: number[] = [];
          inputs.forEach((el, idx) => { try { if (el.checked) checkedIndexes.push(idx); } catch (e) { /* noop */ } });
          if (checkedIndexes.length > 1) {
            // checked の中でラベル可視なものを優先
            let preferred = -1;
            for (let ci = 0; ci < checkedIndexes.length; ci++) {
              const idx = checkedIndexes[ci];
              const el = inputs[idx];
              try {
                if (el.id) {
                  const lab = form.querySelector<HTMLLabelElement>(`label[for="${el.id}"]`);
                  if (lab) {
                    const cs = getComputedStyle(lab);
                    const rect = lab.getBoundingClientRect();
                    if (cs.display !== 'none' && cs.visibility !== 'hidden' && rect.width > 0 && rect.height > 0) {
                      preferred = idx;
                      break;
                    }
                  }
                }
              } catch (e) { /* noop */ }
            }
            if (preferred < 0) preferred = checkedIndexes[0];
            inputs.forEach((el, idx) => { try { el.disabled = !(idx === preferred); } catch (e) { /* noop */ } });
          } else if (checkedIndexes.length === 1) {
            const keep = checkedIndexes[0];
            inputs.forEach((el, idx) => { try { el.disabled = !(idx === keep); } catch (e) { /* noop */ } });
          } else {
            // どれもチェックされていなければ全て有効にしておく
            inputs.forEach((el) => { try { el.disabled = false; } catch (e) { /* noop */ } });
          }
        }
      });
    });
  } catch (err) {
    // noop
  }
}

// リサイズ時に debounce して再評価
let __disableHiddenInputsResizeTimer: number | null = null;
// change イベントでの再評価用タイマー
let __disableHiddenInputsChangeTimer: number | null = null;
window.addEventListener('resize', () => {
  try {
    if (__disableHiddenInputsResizeTimer) window.clearTimeout(__disableHiddenInputsResizeTimer);
    __disableHiddenInputsResizeTimer = window.setTimeout(() => {
      disableHiddenDuplicateInputs();
    }, 120) as unknown as number;
  } catch (e) { /* noop */ }
});

// プロジェクト検索 選択解除した項目が残る問題解消 end



window.addEventListener('pageshow', (e: PageTransitionEvent) => {
  // pageshow はブラウザバックで bfcache から復帰したときにも呼ばれる
  // hover 見た目の復元を防ぐため一時的にクラスを追加して再評価させる
  try {
    document.body.classList.add('no-hover');
    // ごく短時間後に解除して hover の再評価を誘発
    setTimeout(() => document.body.classList.remove('no-hover'), 50);
  } catch (err) {
    // noop
  }
  restoreSubmitButtonsAfterNavigation();
  // 注：disableHiddenDuplicateInputs は初期レンダリング時（ブラウザのレイアウト確定やアコーディオンの開閉タイミング）に誤って input を無効化してしまうことがあります。pageshow で自動実行しないようにし、リサイズ時に再評価するか、必要なときに手動で実行してください。
  // try { disableHiddenDuplicateInputs(); } catch (e) { /* noop */ }
});

// 緊急アラートを閉じる（.emergency 内の button をクリックすると非表示にする）
function initEmergencyClose() {
  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as Element | null;
    if (!target || !(target instanceof Element)) return;
    const btn = target.closest('.emergency > button') as HTMLElement | null;
    if (!btn) return;
    const emergency = btn.closest('.emergency') as HTMLElement | null;
    if (!emergency) return;
    try {
      emergency.classList.add('hidden');
    } catch (err) {
      (emergency as HTMLElement).style.display = 'none';
    }
  });
}


// function initCommonBehaviors() {
//   const $SubmitButtons = document.querySelectorAll('.submit-button');
//   if ($SubmitButtons.length) {
//     $SubmitButtons.forEach(($SubmitButton) => {
//       $SubmitButton.addEventListener('click', (e) => {
//         e.preventDefault();
//         const { target } = e;
//         if (target instanceof HTMLButtonElement) {
//           target.disabled = true;
//           target.innerText = 'Processing...';
//           const formId = target.dataset.form_id;
//           if (!formId) return;
//           const $form = document.getElementById(formId);
//           if ($form && $form instanceof HTMLFormElement) {
//             $form.submit();
//           }
//         }
//       });
//     });
//   }
// }

/*
|--------------------------------------------------------------------------
| Vue Components
|--------------------------------------------------------------------------
|
| description
|
*/
import { createApp } from 'vue';
// import { key, store } from '@/ts/console/store';
// import { router } from '@/ts/console/router';
import VueLikedButton from '@/vue/public/VueLikedButton.vue';
import VueNewsPage from '@/vue/public/VueNewsPage.vue';
import VueCalendarView from '@/vue/public/VueCalendarView.vue';
import VueFollowButton from '@/vue/public/VueFollowButton.vue';
import VueFollowList from '@/vue/public/VueFollowList.vue';
import VueProjectReaction from '@/vue/public/VueProjectReaction.vue';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

function initViewApps() {
  if (document.getElementById('likeButton')) {
    const vueLikeButtonApp = createApp({
      components: {
        VueLikedButton,
      },
    });
    vueLikeButtonApp.mount('#likeButton');
  }
  if (document.getElementById('newsPage')) {
    const vueNewsPageApp = createApp({
      components: {
        VueNewsPage,
      },
    });
    vueNewsPageApp.mount('#newsPage');
  }
  if (document.getElementById('calendarView')) {
    const vueCalendarViewApp = createApp(
      {
        components: {
          VueCalendarView,
        },
      },
    );
    vueCalendarViewApp.mount('#calendarView');
  }

  if (document.getElementById('followButton')) {
    const vueFollowButtonApp = createApp({
      components: {
        VueFollowButton,
      },
    });
    vueFollowButtonApp.mount('#followButton');
  }

  if (document.getElementById('followList')) {
    const vueFollowListApp = createApp({
      components: {
        VueFollowList,
      },
    });
    vueFollowListApp.mount('#followList');
  }

  if (document.getElementById('projectReaction')) {
    const vueProjectReactionApp = createApp({
      components: {
        VueProjectReaction,
      },
    });
    vueProjectReactionApp.mount('#projectReaction');
  }
}


/*
|--------------------------------------------------------------------------
| Swiper
|--------------------------------------------------------------------------
|
| description
|
*/
function initSwiper() {
  if (document.getElementById('kvSwiper')) {
    // 各 .swiper 要素ごとに Swiper インスタンスを作り、配列で保持する
    const els = Array.from(document.querySelectorAll<HTMLElement>('.swiper'));
    const instances: Array<{ el: HTMLElement; inst: any }> = [];
    els.forEach((el) => {
      const inst = new Swiper(el as any, {
        modules: [Autoplay, Navigation, Pagination],
        loop: true,
        loopAdditionalSlides: 1,
        edgeSwipeDetection: false,
        speed: 2000,
        // autoplay は reveal 終了後に手動で開始するため初期は無効
        autoplay: false,
      });
      // 事後に使う autoplay パラメータを保持しておく
      (inst.params as any).autoplay = {
        delay: 6000,
        disableOnInteraction: false,
        waitForTransition: false,
        reverseDirection: true,
      };
      // トランジション時に画像アニメを一時停止するハンドラを追加
      attachSwiperTransitionHandlers(el, inst);
      instances.push({ el, inst });
    });
    (window as any).kvSwipers = instances;
  }
}

// Helpers: toggle a class on the swiper element while a slide transition is occurring
function setSlideTransitioning(el: HTMLElement | null, flag: boolean) {
  if (!el) return;
  try {
    if (flag) el.classList.add('is-slide-transitioning');
    else el.classList.remove('is-slide-transitioning');
  } catch (e) { /* noop */ }
}

function attachSwiperTransitionHandlers(el: HTMLElement | null, inst: any) {
  if (!el || !inst || typeof inst.on !== 'function') return;
  try {
    inst.on('slideChangeTransitionStart', () => setSlideTransitioning(el, true));
    inst.on('slideChangeTransitionEnd', () => setSlideTransitioning(el, false));
    inst.on('transitionStart', () => setSlideTransitioning(el, true));
    inst.on('transitionEnd', () => setSlideTransitioning(el, false));
  } catch (e) {
    // noop
  }
}

// // Swiper の autoplay を開始し、slideShift アニメーションを有効化する
// function startSwiperAutoplay() {
//   // CSS の slideShift を有効にする
//   document.querySelectorAll<HTMLElement>('.swiper').forEach((el) => el.classList.add('is-slideShift-enabled'));
//   const kvSwipers = (window as any).kvSwipers || [];
//   if (!kvSwipers.length) return;

//   // autoplay delay を 6000ms (非スライド表示4s + 移動2s) に統一し、
//   // 各インスタンスの autoplay を確実に開始する。
//   const autoplayCfg = {
//     delay: 6000,
//     disableOnInteraction: false,
//     waitForTransition: false,
//     reverseDirection: true,
//   };

//   const arr = (window as any).kvSwipers as Array<{ el: HTMLElement; inst: any }>;
//   arr.forEach((item, idx) => {
//     const { el, inst } = item;
//     try {
//       // まず既存インスタンスが autoplay を持つか確認
//       if (inst && typeof inst.autoplay?.start === 'function') {
//         try { (inst.params as any).autoplay = autoplayCfg; } catch (e) { /* noop */ }
//         try { inst.autoplay.stop?.(); inst.autoplay.start?.(); } catch (e) { /* noop */ }
//         return;
//       }

//       // 既存インスタンスに autoplay がない場合は当該要素で再生成
//       const currentIndex = (inst?.realIndex ?? inst?.activeIndex ?? 0) as number;
//       try { inst?.destroy(true, true); } catch (e) { /* noop */ }
//       const newInst = new Swiper(el as any, {
//         modules: [Autoplay, Navigation, Pagination],
//         loop: true,
//         loopAdditionalSlides: 1,
//         edgeSwipeDetection: false,
//         speed: 2000,
//         autoplay: autoplayCfg,
//       });
//       attachSwiperTransitionHandlers(el, newInst);
//       try { if (typeof newInst.autoplay?.start === 'function') newInst.autoplay.start(); } catch (e) { /* noop */ }
//       // 配列を更新
//       arr[idx] = { el, inst: newInst };
//       try { if (typeof newInst.slideToLoop === 'function') newInst.slideToLoop(currentIndex, 0, false); } catch (e) { /* noop */ }
//     } catch (err) {
//       // noop
//     }
//   });
// }

// swipers の reveal transition が終わるのを待つ（全て終わるかタイムアウトで解決）
function waitForSwipersRevealEnd(selectors: string[], timeout = 1500): Promise<void> {
  return new Promise((resolve) => {
    const elems = selectors.map(s => document.querySelector<HTMLElement>(s)).filter(Boolean) as HTMLElement[];
    if (!elems.length) return resolve();
    let finished = 0;
    const handlers: Array<(e: TransitionEvent) => void> = [];
    elems.forEach((el, idx) => {
      const handler = (e: TransitionEvent) => {
        if (e.propertyName !== 'opacity' && e.propertyName !== 'transform') return;
        // 二重カウントを避けるため、この要素のリスナーは解除する
        el.removeEventListener('transitionend', handlers[idx] as EventListener);
        finished++;
        if (finished >= elems.length) resolve();
      };
      handlers.push(handler);
      el.addEventListener('transitionend', handler as EventListener);
    });
    setTimeout(() => resolve(), timeout);
  });
}

/*
|--------------------------------------------------------------------------
| form
|--------------------------------------------------------------------------
|
| description
|
*/
// searchStory
function updateKeywordInputState() {
  const isPC = window.innerWidth >= 1024;
  const keywordPcInput = document.getElementById('keyword__pc') as HTMLInputElement | null;
  const keywordSpInput = document.getElementById('keyword__sp') as HTMLInputElement | null;
  if (keywordPcInput) keywordPcInput.disabled = !isPC;
  if (keywordSpInput) keywordSpInput.disabled = isPC;
}

// submit 前に keyword の同名複数入力が混在している場合、送信されるのは1つだけになるように名前を整理する
function prepareKeywordInputs(formId?: string) {
  try {
    const scope: Document | HTMLElement = formId ? (document.getElementById(formId) as HTMLElement) || document : document;
    const inputs = Array.from(scope.querySelectorAll<HTMLInputElement>('input[name="keyword"]'));
    if (!inputs.length) return;

    const firstNonEmptyVisible = inputs.find((inp) => {
      try {
        const cs = window.getComputedStyle(inp);
        const isVisible = cs && cs.display !== 'none' && cs.visibility !== 'hidden' && inp.offsetParent !== null;
        return isVisible && (inp.value || inp.defaultValue || '').toString().trim().length > 0;
      } catch (e) { return false; }
    });

    const preferred = firstNonEmptyVisible || inputs.find((inp) => {
      try {
        const cs = window.getComputedStyle(inp);
        return cs && cs.display !== 'none' && cs.visibility !== 'hidden' && inp.offsetParent !== null;
      } catch (e) { return false; }
    }) || inputs[0];

    // preferred 以外は name 属性を削除して query に含まれないようにする
    inputs.forEach((inp) => {
      try {
        if (inp === preferred) {
          inp.name = 'keyword';
        } else {
          inp.removeAttribute('name');
        }
      } catch (e) { /* noop */ }
    });
  } catch (e) { /* noop */ }
}




/*
|--------------------------------------------------------------------------
| Checkbox
|--------------------------------------------------------------------------
|
| description
|
*/
/**
 * フィルター用チェックボックス（タグ / カテゴリ等）をまとめて制御する。
 *
 * - 「すべて」(All) = "絞り込み条件なし"（= フィルタ解除）
 * - 「すべて」をONにしても、他の項目を全部ONにはしない
 * - 個別項目を1つでもONにしたら「すべて」はOFF
 *
 * - wrapper（.checkbox-wrapper）単位で、各グループを独立して扱う
 * - data-select-all または value="0" を持つ checkbox を「すべて」として検出する
 */
function initCheckboxGroups() {
  const wrappers = document.querySelectorAll<HTMLElement>('.checkbox-wrapper');
  // console.log(1)
  if (!wrappers.length) return;
  wrappers.forEach((wrap) => {
    // data-mode により挙動を切り替える。
    // - search   : 「すべて」= 絞り込み解除（他を全部OFF）
    // - register : 「すべて」= 全選択（他を全部ON）
    const mode = (wrap.dataset.mode ?? 'search') as 'search' | 'register';
    // console.log(2)
    // 「すべて」を表す checkbox の探し方：
    // - data-select-all 属性が付いているもの
    // - または value="0"（現状のマークアップ互換のため）
    const allSelector = 'input[type="checkbox"][data-select-all], input[type="checkbox"][value="0"]';
    // console.log(allSelector)
    const all = wrap.querySelector<HTMLInputElement>(allSelector);
    // console.log(all)
    // この wrapper 内にある checkbox を全部集める
    const boxes = Array.from(wrap.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
    // console.log(boxes)
    if (!boxes.length) return;
    // 「すべて」以外の checkbox（個別項目）だけを取り出す
    const others = all ? boxes.filter((b) => b !== all) : boxes;
    // console.log(others)

    if (all) {
      // 「すべて」チェックボックスの挙動を mode によって切り替える
      all.addEventListener(
        'change',
        () => {
          if (mode === 'search') {
            // 検索：all = 条件なし（絞り込み解除）
            // → all ON で他を全部 OFF（全選択にはしない）
            if (all.checked) {
              others.forEach((b) => {
                b.checked = false;
              });
            }
            // all を OFF にした場合は何もしない（ユーザーが個別に選び直す）
            return;
          }

          // 登録：all = 全選択
          if (all.checked) {
            others.forEach((b) => {
              b.checked = true;
            });
          } else {
            others.forEach((b) => {
              b.checked = false;
            });
          }
        },
        { passive: true }
      );
    }

    // 個別項目が変更されたときに「すべて」のON/OFF状態を同期する
    others.forEach((b) => {
      b.addEventListener(
        'change',
        () => {
          if (!all) return;

          // const anyOn = others.some((x) => x.checked);
          // const noneOn = !anyOn;

          if (mode === 'search') {
            const anyOn = others.some((x) => x.checked);
            const noneOn = !anyOn;

            // 個別が1つでもONなら「すべて」はOFF
            if (anyOn) {
              all.checked = false;
              return;
            }

            // 個別が全部OFFなら「すべて」をON（=条件なし）
            // if (noneOn) {
            //   all.checked = true;
            // }
            return;
          }

          // 登録：個別の状態に合わせて all を同期
          // - 全部ONなら all ON
          // - 1つでもOFFなら all OFF
          const allOn = others.every((x) => x.checked);
          all.checked = allOn;
        },
        { passive: true }
      );
    });
  });
}

/**
 * 「秋田県全域」チェックの特別処理。
 *
 * 要件：
 * - UI上は市町村のチェックを全部ONにしない（色だらけ防止）
 * - ただし検索結果は「秋田県内のみ」にしたい
 *   （県外/オンラインは含めない）
 *
 * - 「秋田県全域」をONにしたら、画面上の市町村チェックは全部OFFのままにする
 * - 代わりに、フォーム送信時だけ hidden input を追加して
 *   `cities[]` に "秋田県内の市町村ID" をまとめて送る
 *   → バックエンド側は通常の cities[] フィルタとして扱える
 *
 * 注意：
 * - hidden input は「秋田県全域」をOFFにしたり、ユーザーが個別チェックを触ったら削除する
 * - hidden input を削除しないと「見た目はOFFなのに絞り込みが残る」状態になるため重要
 */
function initAkitaAllInterests() {
  // PC用(#interest_akita_all) / SP用(#sp__all) の「秋田県全域」チェックをまとめて取得
  const akitaAlls = document.querySelectorAll<HTMLInputElement>('#interest_akita_all, #sp__all');

  if (!akitaAlls.length) return;

  // 市町村（エリア）用の checkbox 一覧。class="interest-city" を目印に取得している
  const regionBoxes = Array.from(document.querySelectorAll<HTMLInputElement>('input.interest-city'));

  // 「県外」「オンライン」は秋田県全域の対象外なので、ラベル文字で判定して除外する
  // （DBのIDではなく表示テキストで判定しているため、文言変更時はここも要注意）
  const isExcluded = (cb: HTMLInputElement) => {
    const label = document.querySelector(`label[for="${cb.id}"]`);
    const labelText = label?.textContent?.trim() ?? '';
    return labelText === '県外' || labelText === 'オンライン';
  };

  // 秋田県全域に含める対象（= 県外・オンライン以外）
  const eligibleBoxes = regionBoxes.filter(cb => !isExcluded(cb));

  // すべての対象(eligible)がONかどうか
  const allEligibleChecked = () => eligibleBoxes.every(cb => cb.checked);

  // hidden input をまとめて追加/削除するための "目印" 属性
  const HIDDEN_WRAP_ATTR = 'data-akita-all-hidden';

  // 以前追加した hidden input 群を削除する（秋田県全域OFFや個別選択開始時に呼ぶ）
  const removeHiddenCityInputs = (form: HTMLFormElement | null) => {
    if (!form) return;
    try {
      form.querySelectorAll<HTMLElement>(`[${HIDDEN_WRAP_ATTR}]`).forEach((el) => el.remove());
    } catch (e) {
      // noop
    }
  };

  // eligible(秋田県内) の city ID を hidden input としてフォームに差し込む
  // → UIのチェック状態と無関係に、サーバーへ cities[] を送れるようにする
  const injectHiddenCityInputs = (form: HTMLFormElement | null) => {
    if (!form) return;
    // 既存の hidden を一旦消して入れ直す
    removeHiddenCityInputs(form);

    // パラメータ名（例: cities[]）は、実際の checkbox が持っている name を優先する
    // これにより、マークアップ側の name 変更に強くなる
    const paramName = eligibleBoxes.find((b) => typeof b.name === 'string' && b.name.length > 0)?.name || 'cities[]';

    // hidden をまとめるコンテナを作り、後で一括削除できるようにする
    const wrap = document.createElement('div');
    wrap.setAttribute(HIDDEN_WRAP_ATTR, '1');
    wrap.style.display = 'none';

    // eligible の数だけ <input type="hidden" name="cities[]" value="..."> を作って追加
    eligibleBoxes.forEach((cb) => {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = paramName;
      hidden.value = cb.value;
      wrap.appendChild(hidden);
    });

    // 検索時に「秋田県全域」が選ばれていることをサーバー側で判定できるよう
    // フラグ用の hidden input を追加する（テンプレートはこれを使って個別チェックを描画しない）
    const allFlag = document.createElement('input');
    allFlag.type = 'hidden';
    allFlag.name = 'cities_all';
    allFlag.value = '1';
    wrap.appendChild(allFlag);

    form.appendChild(wrap);
  };

  // 「秋田県全域」のON/OFFに応じて、UIの見た目と送信パラメータ（hidden）を切り替える
  // 秋田県全域の切り替え
  // 「秋田県全域」は「絞り込み解除（条件なし）」として扱う
  akitaAlls.forEach((akitaAll) => {
    akitaAll.addEventListener(
      'change',
      () => {
        // どのフォームに hidden を追加するか：チェックボックスが属している form を探す
        const form = (akitaAll.closest('form') as HTMLFormElement | null) || null;

        // この「秋田県全域」が属するグループが search/register どちらかを判定
        const mode = (akitaAll.closest('.checkbox-wrapper') as HTMLElement | null)?.dataset.mode ?? 'search';

        if (akitaAll.checked) {
          if (mode === 'register') {
            // 登録：UI上で実際にチェックを入れる（全選択）
            eligibleBoxes.forEach((cb) => {
              cb.checked = true;
            });
            // 登録画面では hidden を使わない
            removeHiddenCityInputs(form);
            return;
          }

          // 検索：見た目はOFFのまま、送信だけ秋田県内のみになるよう hidden を注入
          eligibleBoxes.forEach((cb) => {
            cb.checked = false;
          });

          // 見た目はOFFのままでも、送信だけは「秋田県内のみ」になるよう hidden を注入
          injectHiddenCityInputs(form);
          return;
        }

        // 秋田県全域をOFFにしたら hidden を消して、通常の個別選択の挙動に戻す
        removeHiddenCityInputs(form);
      },
      { passive: true }
    );
  });

  // ユーザーが個別の市町村（県外/オンライン含む）を触ったときは、
  // 秋田県全域チェックと hidden を適切に解除して「ユーザーの意思」を優先する
  // 個別チェック時：「秋田県全域」を同期
  regionBoxes.forEach((cb) => {
    cb.addEventListener(
      'change',
      () => {
        // どのモードか（search/register）を各チェックから判定
        const mode = (cb.closest('.checkbox-wrapper') as HTMLElement | null)?.dataset.mode ?? 'search';

        if (mode === 'register') {
          // 登録：eligible が全部ON なら 秋田県全域 ON、1つでもOFFなら OFF
          const eligibleAllOn = eligibleBoxes.every((x) => x.checked);
          const excludedAllOff = regionBoxes.filter(isExcluded).every((x) => !x.checked);
          const shouldOn = eligibleAllOn && excludedAllOff;

          akitaAlls.forEach((akitaAll) => {
            akitaAll.checked = shouldOn;
            const form = (akitaAll.closest('form') as HTMLFormElement | null) || null;
            removeHiddenCityInputs(form);
          });
          return;
        }

        // 検索：除外項目（県外/オンライン）を無視して判定する
        // → 除外を押しても akitaAll は解除されないようにする
        const anyOn = eligibleBoxes.some((x) => x.checked);
        const noneOn = !anyOn;

        // 個別が1つでもONなら「秋田県全域」はOFF
        if (anyOn) {
          akitaAlls.forEach((akitaAll) => {
            akitaAll.checked = false;
            const form = (akitaAll.closest('form') as HTMLFormElement | null) || null;
            removeHiddenCityInputs(form);
          });
          return;
        }

        // 個別が全部OFFなら「秋田県全域」をON（=条件なし）
        // if (noneOn) {
        //   akitaAlls.forEach((akitaAll) => {
        //     akitaAll.checked = true;
        //     const form = (akitaAll.closest('form') as HTMLFormElement | null) || null;
        //     removeHiddenCityInputs(form);
        //   });
        // }
      },
      { passive: true }
    );
  });

  // ページ読み込み時に既に「秋田県全域」がチェックされている場合は
  // hidden を注入しておく（サーバーが cities_all を反映して描画しているが
  // client 側の hidden がないと再検索時に送信されないため）。
  try {
    akitaAlls.forEach((akitaAll) => {
      try {
        const form = (akitaAll.closest('form') as HTMLFormElement | null) || null;
        const mode = (akitaAll.closest('.checkbox-wrapper') as HTMLElement | null)?.dataset.mode ?? 'search';
        if (mode === 'search' && akitaAll.checked) {
          injectHiddenCityInputs(form);
        }
      } catch (e) { /* noop */ }
    });
  } catch (e) { /* noop */ }
}

/*
|--------------------------------------------------------------------------
| Share copy (clipboard)
|--------------------------------------------------------------------------
|
| ボタンをクリックしたら同一ラップ内の input[type="text"] の value を
| クリップボードにコピーする。ページ内イベントデリゲーションで監視。
|
*/
function initShareCopy() {
  document.addEventListener('click', (e: Event) => {
    const target = (e.target as Element | null);
    const btn = target
      ? (target.closest('.projectSingle__shareCopyBtn, .common__shareCopyBtn') as HTMLElement | null)
      : null;
    if (!btn) return;
    const wrap = btn.closest('.projectSingle__shareCopy, .common__shareCopy') as HTMLElement | null;
    if (!wrap) return;
    const input = wrap.querySelector<HTMLInputElement>('input[type="text"]');
    if (!input) return;
    const text = input.value || '';
    const inputEl = input; // 型推論のためローカルに束縛

    const showFeedback = (message: string) => {
      // このラップ内の .projectSingle__shareCopyInr の直後に挿入する
      // まずラップ直後に既にフィードバック要素があるか確認
      let fb: HTMLElement | null = null;
      const nextEl = wrap.nextElementSibling as HTMLElement | null;
      if (
        nextEl
        && (
          nextEl.classList.contains('projectSingle__shareCopyFeedback')
          || nextEl.classList.contains('common__shareCopyFeedback')
          || nextEl.classList.contains('shareCopyFeedback')
        )
      ) {
        fb = nextEl;
      }

      if (!fb) {
        fb = document.createElement('div');
        fb.className = 'projectSingle__shareCopyFeedback common__shareCopyFeedback shareCopyFeedback';
        fb.setAttribute('aria-live', 'polite');
        // 簡易スタイル（必要なら Sass 側で上書きしてください）
        fb.style.display = 'block';
        fb.style.width = '100%';
        fb.style.marginTop = '6px';
        fb.style.marginLeft = '0';
        fb.style.fontSize = '14px';
        fb.style.color = '#3B3B3B';

        const inr = wrap.querySelector<HTMLElement>('.projectSingle__shareCopyInr, .common__shareCopyInr');
        const insertAfter = inr || wrap;
        if (insertAfter && insertAfter.parentNode) {
          insertAfter.parentNode.insertBefore(fb, insertAfter.nextSibling);
        } else if (btn.parentNode) {
          // フォールバック: ボタンの直後
          btn.parentNode.insertBefore(fb, btn.nextSibling);
        }
      }

      fb.textContent = message;
      // 自動で消す
      setTimeout(() => { if (fb) fb.textContent = ''; }, 1500);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        showFeedback('コピーしました');
      }).catch(() => {
        fallbackCopy();
      });
    } else {
      fallbackCopy();
    }

    function fallbackCopy() {
      try {
        inputEl.select();
        // iOS 等で必要な場合があるためレンジも設定
        inputEl.setSelectionRange(0, inputEl.value.length);
        document.execCommand('copy');
        showFeedback('コピーしました');
      } catch (err) {
        // フォールバックでも失敗したらユーザーに通知
        // eslint-disable-next-line no-alert
        alert('コピーに失敗しました。手動で選択してコピーしてください。');
      }
      try { window.getSelection()?.removeAllRanges(); } catch (e) { /* noop */ }
    }
  }, false);
}


/*
|--------------------------------------------------------------------------
| お知らせ一覧 「カテゴリで探す」「エリアで探す」 切り替えタブ
|--------------------------------------------------------------------------
|
| description
|
*/
function initNewsTabSwitch() {
  // メインタブ
  const tabButtons = document.querySelectorAll<HTMLButtonElement>('.news__tab button');
  const tabPanels = document.querySelectorAll<HTMLElement>('.news__tab--content .news__tab--panel');
  if (!tabButtons.length || !tabPanels.length) return;

  tabButtons.forEach((btn) => {
    btn.addEventListener('click', function (this: HTMLButtonElement) {
      // ボタンのactive切り替え
      tabButtons.forEach((b) => b.classList.remove('active'));
      this.classList.add('active');

      // パネルの表示切り替え
      const tab = this.getAttribute('data-tab');
      tabPanels.forEach((panel) => {
        if (panel.getAttribute('data-tab') === tab) {
          panel.setAttribute('style', 'display:block;');
        } else {
          panel.setAttribute('style', 'display:none;');
        }
      });
    });
  });

}

// サブタブのactive切り替え
function initNewsSubtabSwitch() {
  const subtabButtons = document.querySelectorAll<HTMLButtonElement>('.news__subtab button');
  if (!subtabButtons.length) return;
  subtabButtons.forEach((btn) => {
    btn.addEventListener('click', function (this: HTMLButtonElement) {
      subtabButtons.forEach((b) => b.classList.remove('active'));
      this.classList.add('active');
    });
  });
}


/*
|--------------------------------------------------------------------------
| on Loaded
|--------------------------------------------------------------------------
|
| description
|
*/
/**
 * DOMのロードが完了したら
 */
function init() {
  initCommonBehaviors();
  initEmergencyClose();
  initViewApps();
  initSwiper();
  initCheckboxGroups();
  initAkitaAllInterests();
  initShareCopy();
  updateKeywordInputState();
  // 表示されない重複 input を disabled にして送信値の混在を防ぐ
  // NOTE: 初期レンダリングで誤検出することがあるため、自動実行は行わず
  // ユーザーがフィルタ操作したときに再評価する（debounce）
  try {
    const form = document.getElementById('searchStoryForm') as HTMLFormElement | null;
    if (form) {
      form.addEventListener('change', () => {
        try {
          if (__disableHiddenInputsChangeTimer) window.clearTimeout(__disableHiddenInputsChangeTimer);
          __disableHiddenInputsChangeTimer = window.setTimeout(() => {
            try { disableHiddenDuplicateInputs(); } catch (e) { /* noop */ }
          }, 50) as unknown as number;
        } catch (e) { /* noop */ }
      }, { passive: true });
      // submit 前にキーワードの重複を整理して混在を防ぐ
      form.addEventListener('submit', () => {
        try { prepareKeywordInputs(form.id || undefined); } catch (e) { /* noop */ }
        try { disableHiddenDuplicateInputs(); } catch (e) { /* noop */ }
      });
    }
  } catch (e) { /* noop */ }
  initClearFilters();
  // initNewsTabSwitch();
  // initNewsSubtabSwitch();
  // ページにスピナーや main がない（または既に DOM 表示される構成）の場合は
  // DOMContentLoaded の時点でヘッダーを表示する（トップページは除外）
  try {
    const spinner = document.getElementById('spinner');
    const mainEl = document.getElementById('main');
    if (!spinner || !mainEl) {
      ensureHeaderVisibleOnNonTop();
    }
  } catch (e) { /* noop */ }
}
document.addEventListener('DOMContentLoaded', init);

// すべてクリアボタンの初期化
function initClearFilters() {
  const btn = document.getElementById('clearFiltersBtn') as HTMLButtonElement | null;
  if (!btn) return;
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    // フォームを取得
    const form = document.getElementById('searchStoryForm') as HTMLFormElement | null;
    if (!form) return;

    // チェックボックス／ラジオを全部外す（change イベントは発火させない）
    form.querySelectorAll<HTMLInputElement>('input[type="checkbox"], input[type="radio"]').forEach((i) => {
      try { i.checked = false; } catch (err) { /* noop */ }
    });

    // 明示的に「すべて」チェック（data-select-all / value="0"）を OFF にする
    form.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-select-all], input[type="checkbox"][value="0"]').forEach((a) => {
      try { a.checked = false; } catch (err) { /* noop */ }
    });

    // テキスト入力をクリア
    form.querySelectorAll<HTMLInputElement>('input[type="text"], input[type="search"], textarea').forEach((i) => {
      try { i.value = ''; } catch (err) { /* noop */ }
    });

    // hidden のうち akita-all や配列フィルタのものを削除
    try {
      // akita の hidden をまとめて削除するマークがあるコンテナを削除
      form.querySelectorAll<HTMLElement>('[data-akita-all-hidden]').forEach((el) => el.remove());
      // name が配列形式の hidden はフィルタ系なので削除（例: tags[], cities[]）
      form.querySelectorAll<HTMLInputElement>('input[type="hidden"]').forEach((h) => {
        try {
          if (/\[\]$/.test(h.name || '')) h.remove();
        } catch (err) { /* noop */ }
      });
    } catch (err) { /* noop */ }

    // select を初期化（選択解除）
    form.querySelectorAll<HTMLSelectElement>('select').forEach((s) => {
      try { s.selectedIndex = -1; } catch (err) { /* noop */ }
    });

    // submit はしない — ユーザーが再度探すを押すまで待つ
    // クリア操作は programmatic にチェック状態を変えるため change イベントが発火しない。
    // そのため表示されない重複 input の disabled 再評価を明示的に実行する。
    try {
      if (__disableHiddenInputsChangeTimer) window.clearTimeout(__disableHiddenInputsChangeTimer);
      __disableHiddenInputsChangeTimer = window.setTimeout(() => {
        try { disableHiddenDuplicateInputs(); } catch (e) { /* noop */ }
      }, 10) as unknown as number;
    } catch (e) { /* noop */ }
  }, { passive: true });
}



/**
 * ページのロードが完了したら
 */
const $spinner = document.getElementById('spinner');
const $main = document.getElementById('main');
window.addEventListener('load', (event: Event) => {
  if (!$spinner || !$main) return;
  setTimeout(() => {
    $spinner.remove();
    $main.style.opacity = '1';
    // スピナー解除と同時に、トップページ以外ではヘッダーを即時表示する
    try { ensureHeaderVisibleOnNonTop(); } catch (e) { /* noop */ }
  }, 200);
});

/*---------------------------*/
/* kvアニメーション */
/*--------------------------*/
// ページのロードが完了したら.2秒後に.kv__decoを表示（初回のみアニメ、2回目以降は即時アニメ後状態）
window.addEventListener('load', () => {
  setTimeout(() => {
    const isPC = window.innerWidth >= 1024;
    const deco = isPC
      ? document.querySelector<HTMLElement>('.kv__deco--pc')
      : document.querySelector<HTMLElement>('.kv__deco--sp');
    const kv = document.querySelector<HTMLElement>('.kv');
    if (!kv) return;

    // 2回目以降はアニメ後の状態を即時反映
    if (localStorage.getItem('kv_animated')) {
      kv.classList.add('is-play--noanim', 'is-play', 'is-play-stage2');
      const inr = kv.querySelector<HTMLElement>('.kv__txt--showBottomInr');
      if (inr) inr.classList.add('is-open');
      // .kv__img1〜.kv__img14 すべてにアニメ後のクラスを付与
      for (let i = 1; i <= 14; i++) {
        const img = document.querySelector<HTMLElement>(`.kv__img${i}`);
        if (img) img.classList.add('is-visible-from-bottom', 'is-float-start');
        const txt = document.querySelector<HTMLElement>(`.kv__txt${i}`);
        if (txt) txt.style.opacity = '1';
      }
      const header = document.querySelector<HTMLElement>('header.header');
      if (header) header.classList.add('is-visible', 'is-visible--noanim');
      if (deco) deco.classList.add('is-visible');
      return;
    }

    // 初回のみアニメーション実行
    if (deco) {
      deco.classList.add('is-visible');
      if (kv) {
        const handler = (e: Event) => {
          const te = e as TransitionEvent;
          if (te.propertyName === 'opacity') {
            kv.classList.add('is-play');
            startKvShowBottomAfterTxtAnim(kv);
            deco.removeEventListener('transitionend', handler as EventListener);
          }
        };
        deco.addEventListener('transitionend', handler as EventListener);
        setTimeout(() => {
          if (!kv.classList.contains('is-play')) {
            kv.classList.add('is-play');
            startKvShowBottomAfterTxtAnim(kv);
          }
        }, 700);
      }
    }

    // kvアニメーション終了時にlocalStorageへフラグをセット
    // kv__txt13のアニメーション終了時 or 最終段階で
    const img13 = document.querySelector<HTMLImageElement>('.kv__txt13');
    if (img13) {
      const onAnimEnd = (e: AnimationEvent) => {
        if (typeof e.animationName === 'string' && e.animationName.indexOf('kv-img-fade') === -1) return;
        localStorage.setItem('kv_animated', '1');
        img13.removeEventListener('animationend', onAnimEnd as EventListener);
      };
      img13.addEventListener('animationend', onAnimEnd as EventListener);
    } else {
      // img13がない場合は、kv__img5のアニメ後など適切なタイミングでセット
      setTimeout(() => {
        localStorage.setItem('kv_animated', '1');
      }, 3000);
    }
  }, 200);
});

// kv__txt1..3 のアニメーション完了を待って
// .kv__txt--showBottomInr に .is-open を付与する
function startKvShowBottomAfterTxtAnim(kv: HTMLElement) {
  if (!kv) return;
  const inr = kv.querySelector<HTMLElement>('.kv__txt--showBottomInr');
  if (!inr) return;
  // 既に開始済みなら何もしない
  if (inr.classList.contains('is-open')) return;

  const imgs = Array.from(kv.querySelectorAll<HTMLImageElement>('img.kv__txt1, img.kv__txt2, img.kv__txt3'));
  if (!imgs.length) {
    inr.classList.add('is-open');
    return;
  }

  let finished = 0;
  const onAnimEnd = (e: AnimationEvent) => {
    // アニメーション名が kv-img-fade のもののみ数える（念のため）
    if (typeof e.animationName === 'string' && e.animationName.indexOf('kv-img-fade') === -1) return;
    finished++;
    if (finished >= imgs.length) {
      inr.classList.add('is-open');
      imgs.forEach(img => img.removeEventListener('animationend', onAnimEnd as EventListener));
    }
  };

  imgs.forEach(img => img.addEventListener('animationend', onAnimEnd as EventListener));

  // フォールバック: animationend が来ない場合に備え、余裕を見たタイムアウトで開始
  setTimeout(() => {
    if (!inr.classList.contains('is-open')) inr.classList.add('is-open');
  }, 900);

  // 「つながる」（kv__txt4..7）のアニメーションが終わったら次の段（kv__txt8..14）を開始する
  const startStage2 = () => {
    let targetKv: HTMLElement | null = kv;
    if (!targetKv) targetKv = document.querySelector<HTMLElement>('.kv');
    if (!targetKv) return;
    // 防止: すでにステージ2開始済みなら何もしない
    if (targetKv.classList.contains('is-play-stage2')) return;
    // 少し遅延を置いて付与（下段のアニメーションと被らないように）
    setTimeout(() => {
      targetKv!.classList.add('is-play-stage2');

      // kv__txt12 のアニメーション完了を待って header を表示
      const img12 = document.querySelector<HTMLImageElement>('.kv__txt12');
      const revealHeader = () => {
        const header = document.querySelector<HTMLElement>('header.header');
        if (header && !header.classList.contains('is-visible')) header.classList.add('is-visible');
      };

        // kv__txt13 のアニメ完了で 画像 を順次下から表示する処理
        const img13 = document.querySelector<HTMLImageElement>('.kv__txt13');
        const showKvImages = () => {
          const seq: Array<{ sel: string; delay: number }> = [
            { sel: '.kv__img1', delay: 0 },
            { sel: '.kv__img3', delay: 0 },
            { sel: '.kv__img2', delay: 100 },
            { sel: '.kv__img5', delay: 150 },
            { sel: '.kv__img4', delay: 350 },
          ];
          seq.forEach((item) => {
            const el = document.querySelector<HTMLElement>(item.sel);
            // console.log(item.sel, el);
            if (!el) return;
            setTimeout(() => el.classList.add('is-visible-from-bottom'), item.delay);
          });
          // すべての画像の表示アニメーションが終わったら浮遊アニメを開始
          const selectors = seq.map(s => s.sel);
          waitForSwipersRevealEnd(selectors, 1600).then(() => {
            // .kv__img1~5にis-float-startクラスを付与
            seq.forEach(item => {
              const el = document.querySelector<HTMLElement>(item.sel);
              if (el) el.classList.add('is-float-start');
            });
          });
        };

        // ripples 再生のフォールバックタイマーID（animationend とフォールバックが二重実行されるのを防ぐ）
        let ripplesFallbackTimer: number | null = null;
        if (img13) {
          const on13 = (e: AnimationEvent) => {
            if (typeof e.animationName === 'string' && e.animationName.indexOf('kv-img-fade') === -1) return;
            // フォールバックタイマーが残っていればクリア
            try { if (ripplesFallbackTimer) { clearTimeout(ripplesFallbackTimer); ripplesFallbackTimer = null; } } catch (err) { /* noop */ }
            // ripples を1回だけ再生
            try {
              const ripple = document.querySelector<HTMLElement>('.ripples');
              if (ripple) {
                ripple.classList.remove('ripples--play');
                // force reflow
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                ripple.offsetWidth;
                ripple.classList.add('ripples--play');
                // cleanup: 再生後にクラスを外す（アニメ完了 2s + max delay 0.5s の余裕）
                setTimeout(() => ripple.classList.remove('ripples--play'), 2600);
              }
            } catch (err) { /* noop */ }
            showKvImages();
            img13.removeEventListener('animationend', on13 as EventListener);
          };
          img13.addEventListener('animationend', on13 as EventListener);
          // フォールバック（animationend が来ない場合） — このタイマーは on13 でクリアされる
          ripplesFallbackTimer = window.setTimeout(() => {
            try {
              const ripple = document.querySelector<HTMLElement>('.ripples');
              if (ripple) {
                ripple.classList.remove('ripples--play');
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                ripple.offsetWidth;
                ripple.classList.add('ripples--play');
                setTimeout(() => ripple.classList.remove('ripples--play'), 2600);
              }
            } catch (err) { /* noop */ }
            showKvImages();
            ripplesFallbackTimer = null;
          }, 2200);
        } else {
          // img13 が存在しない場合は短い遅延で表示
          ripplesFallbackTimer = window.setTimeout(() => {
            try {
              const ripple = document.querySelector<HTMLElement>('.ripples');
              if (ripple) {
                ripple.classList.remove('ripples--play');
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                ripple.offsetWidth;
                ripple.classList.add('ripples--play');
                setTimeout(() => ripple.classList.remove('ripples--play'), 2600);
              }
            } catch (err) { /* noop */ }
            showKvImages();
            ripplesFallbackTimer = null;
          }, 500);
        }

      if (img12) {
        const on12 = (e: AnimationEvent) => {
          if (typeof e.animationName === 'string' && e.animationName.indexOf('kv-img-fade') === -1) return;
          revealHeader();
          img12.removeEventListener('animationend', on12 as EventListener);
        };
        img12.addEventListener('animationend', on12 as EventListener);
        // フォールバック
        setTimeout(() => revealHeader(), 2000);
      } else {
        // img12 がなければ短い遅延で表示
        setTimeout(revealHeader, 500);
      }
    }, 100);
  };

  // 監視: inr が開いたら下段要素のアニメ終了を待つ
  const observeStage1End = () => {
    const stageImgs = Array.from(inr.querySelectorAll<HTMLImageElement>('img.kv__txt4, img.kv__txt5, img.kv__txt6, img.kv__txt7'));
    if (!stageImgs.length) {
      startStage2();
      return;
    }
    let finishedStage1 = 0;
    const onStage1End = (e: AnimationEvent) => {
      finishedStage1++;
      if (finishedStage1 >= stageImgs.length) {
        stageImgs.forEach(img => img.removeEventListener('animationend', onStage1End as EventListener));
        startStage2();
      }
    };
    stageImgs.forEach(img => img.addEventListener('animationend', onStage1End as EventListener));
    // フォールバック: 万一アニメーションイベントが来ない場合
    setTimeout(() => {
      if (!inr.classList.contains('is-open')) return; // まだ開いてない
      if (!document.querySelector('.kv')?.classList.contains('is-play-stage2')) startStage2();
    }, 2000);
  };

  // inr が is-open になったタイミングで監視を開始
  const mo = new MutationObserver((mut) => {
    mut.forEach(m => {
      if (m.type === 'attributes' && (m.target as HTMLElement).classList.contains('is-open')) {
        observeStage1End();
        mo.disconnect();
      }
    });
  });
  mo.observe(inr, { attributes: true });
}


// トップページ（.kv があるページ）かどうかでヘッダー表示を制御する
function ensureHeaderVisibleOnNonTop() {
  try {
    const header = document.querySelector<HTMLElement>('header.header');
    if (!header) return;
    // KV が存在する場合はトップページ扱い → アニメーションは既存のフローに任せる
    if (document.querySelector('.kv')) return;

    // KV がないページではアニメーションを無効化してヘッダーを即時表示
    header.classList.add('is-visible', 'is-visible--noanim');
    // 無効化クラスは短時間後に外す（以降は通常のスタイルに戻す）
    window.setTimeout(() => {
      try { header.classList.remove('is-visible--noanim'); } catch (e) { /* noop */ }
    }, 50);
  } catch (e) {
    // noop
  }
}

/*------------------------------------*/
/* 固定ボタン共通（toTop / follow など） */
/*------------------------------------*/
type FloatingButtonConfig = {
  visibleScrollY?: number;
  baseBottom: number;
  footerGap?: number;
};

function applyFloatingButtonState(button: HTMLElement, config: FloatingButtonConfig) {
  const footer = document.querySelector<HTMLElement>('.footer');
  if (!footer) return;

  const visibleScrollY = config.visibleScrollY ?? 0;
  if (visibleScrollY > 0) {
    if (window.scrollY > visibleScrollY) button.classList.add('is-visible');
    else button.classList.remove('is-visible');
  }

  const footerGap = config.footerGap ?? 0;
  const windowHeight = window.innerHeight;
  const footerRect = footer.getBoundingClientRect();

  if (footerRect.top < windowHeight) {
    const overlap = Math.max(windowHeight - footerRect.top, 0);
    button.style.bottom = `${Math.max(config.baseBottom, overlap + footerGap)}px`;
  } else {
    button.style.bottom = `${config.baseBottom}px`;
  }
}

function initFloatingButtons() {
  const buttons = Array.from(document.querySelectorAll<HTMLElement>('.js-floating-button'));
  if (!buttons.length) return;

  const render = () => {
    buttons.forEach((button) => {
      const config: FloatingButtonConfig = {
        visibleScrollY: Number(button.dataset.visibleScrollY ?? 0),
        baseBottom: Number(button.dataset.baseBottom ?? 40),
        footerGap: Number(button.dataset.footerGap ?? -26),
      };
      applyFloatingButtonState(button, config);
    })
  };

  let ticking = false;
  const onScrollOrResize = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      render();
      ticking = false;
    });
  };

  render();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
}

initFloatingButtons();
window.addEventListener('resize', () => {
  updateKeywordInputState();
});

/*------------------------------------*/
/* バーガーメニュー開閉アニメーション
/*------------------------------------*/
document.addEventListener('DOMContentLoaded', () => {
  const btn = document.getElementById('lines__btn');
  if (btn) {
    btn.addEventListener('click', () => {
      const bars = btn.querySelectorAll('.bar');
      bars.forEach(bar => {
        bar.classList.toggle('open');
      });
    });
  }
});

/*---------------------------------------------*/
/* プロジェクト 「探す」ボタンを押したときの遷移位置 */
/*---------------------------------------------*/
window.addEventListener('DOMContentLoaded', function() {
  if (window.location.hash === '#project__itemsOuter') {
    setTimeout(function() {
      var target = document.getElementById('project__itemsOuter');
      if (target) {
        var header = document.querySelector('header'); // ヘッダーのセレクタに合わせて変更
        var headerHeight = header ? header.offsetHeight : 0;
        var offset = headerHeight + 120;
        var rect = target.getBoundingClientRect();
        var scrollTop = window.pageYOffset + rect.top - offset;
        window.scrollTo({ top: scrollTop, behavior: 'smooth' });
      }
    }, 100); // ページ描画後に実行
  }
});


/*
|--------------------------------------------------------------------------
| Bookmark Notice
|--------------------------------------------------------------------------
|
| description
|
*/
// ブックマーク案内の表示/非表示
function initBookmarkNotice() {
  const notice = document.getElementById('bookmark-notice') as HTMLElement;
  if (!notice) return; // ログイン以外の画面では何もしない

  const KEY = 'bookmark_notice_dismissed';
  if (localStorage.getItem(KEY) === '1') {
    notice.remove();
    return;
  }

  const btn = document.getElementById('bookmark-dismiss');
  if (btn instanceof HTMLElement) {
    btn.addEventListener('click', () => {
      console.log('bookmark-dismiss clicked');
      notice.remove();
      localStorage.setItem(KEY, '1');
    });
  }
}
